import { Component, Inject, OnInit } from "@angular/core";
import { FormGroup, Validators } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { LookupListService } from "@modules/common/services/lookup-list.service";
import { changeDescription, changePrice, changeQuantity, codeOnly, JobLineDto, LookupObjectDto, StockMasterDto } from "@shared/models";
import { ProductSettingService, StockService } from "../../common/services/index";

export interface LineComponentData {
  jobId: string;
  group: FormGroup;
  parentId: string;// [Parent id can be a modelid, job type, diag code, anything.,..]
  warehouseId?: string;
  errors: { [control: string]: string[] };
  onDelete: (line: JobLineDto) => boolean;
  canChangePayment: boolean;
  paymentTypes: string[];
}
@Component({
  templateUrl: "./line-modal.component.html",
  styleUrls: ["./line-modal.component.scss"]
})
export class LineModalComponent implements OnInit {
  formErrors: { [control: string]: string[] };
  group: FormGroup;
  available: number;
  warehouseId: string;
  parentId: string;// mapped from LineComponentData
  private _jobId: string;
  private _oldValue: any;
  exclude: string[] = [];
  lineTypes: LookupObjectDto[];
  canChangePayment: boolean;
  paymentTypes: string[];

  validationMessages = {
    description:
    {
      required: "JobLine.DescriptionRequired",
    },
    typeId:
    {
      required: "JobLine.TypeIDRequired",
      validType: "JobLine.TypeIDInvalid"
    },
  };

  onDelete: (visit: JobLineDto) => boolean = null;
  allowNegativeNumbers = false;
  constructor(
      private activeModal: MatDialogRef<LineModalComponent>,
      @Inject(MAT_DIALOG_DATA) data: LineComponentData,
      private stockService: StockService,
      productSettings: ProductSettingService,
      lookups: LookupListService
    ) {
    this._jobId = data.jobId;
    this.group = data.group;
    this.formErrors = data.errors;
    this.canChangePayment = data.canChangePayment;
    this.paymentTypes = data.paymentTypes;
    this.onDelete = data.onDelete;
    this.exclude.push("MDL");
    this.parentId = data.parentId;
    this.warehouseId = data.warehouseId;
    if (!productSettings.booleanValue("AllowExtraTravelLines")) {
      this.exclude.push("TRV");
    }
    if (!productSettings.booleanValue("AllowExtraLabourLines")) {
      this.exclude.push("LAB");
    }
    lookups.lookupList("CodeStockType").subscribe(x => this.lineTypes = x.values.filter(l => !this.exclude.includes(l.code)));
    this.setFormData(this.group);
    this.allowNegativeNumbers = productSettings.booleanValue("JobLineAllowNegativeNumbers");
  }

  ngOnInit(){
    this.activeModal.updateSize("600px");
  }

  getMode(typeId: string): string {
    return typeId === "TRV"  || typeId === "LAB" ? "adhoc" : "all";
  }

  setFormData(group: FormGroup) {
    this._oldValue = group.getRawValue();
    if (this._oldValue?.itemId[0] === "<") {
      group.patchValue({ itemId: "" });
    }
    group.get("line1").setValidators(Validators.required);
    group.get("itemId").setValidators(Validators.required);
    group.get("itemId").updateValueAndValidity();
    this.toggleControl("amount", false);

    if(this._oldValue.itemId) // itemId (model code) was passed in?
    {
      this.stockService.getStockMaster(codeOnly(this._oldValue.itemId)).subscribe(stockMaster => {
        // Ensure fields are properly enabled according to stock code
        this.updateControlToggles(stockMaster);
      });
    }
  }

  showStock(stock: StockMasterDto) {
    this.stockService.getPrice(stock.code, this._jobId).toPromise().then(data => {
      this.group.patchValue({
        line1: stock.description,
        price: data.price,
        quantityOrdered: 1,
        amount: data.price,
        cost: data.price,
        mode: data.mode,
        vatId: stock.vatId,
        vatRate: stock.vatId ? 0.15 : 0,// TODO: use valid system vat value
      });
      this.updateControlToggles(stock);
    });
  }

  updateControlToggles(stock: StockMasterDto){
    this.available = stock.quantity;
    this.toggleControl("line1", changeDescription(stock));
    this.toggleControl("price", changePrice(stock));
    this.toggleControl("quantityOrdered", changeQuantity(stock));
  }

  private toggleControl(name: string, enabled: boolean) {
    const control = this.group.get(name);
    if (enabled && control.disabled) {
      control.enable();
    } else if (!enabled && control.enabled) {
      control.disable();
    }
  }

  delete() {
    if (this.onDelete) {
      this.onDelete(null);
    }
  }

  cancel() {
    this.group.reset(this._oldValue);
    this.group.get("line1").clearValidators();
    this.group.get("itemId").clearValidators();
    this.group.get("line1").updateValueAndValidity();
    this.group.get("itemId").updateValueAndValidity();
    this.activeModal.close(false);
  }
}
